import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Pencil, MessageSquareQuote, Eye, EyeOff, Star } from 'lucide-react';
import { api } from '../../lib/api';
import type { Testimonial } from '../../types';
import { formatDate } from '../../lib/format';
import { useUpload } from '../../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from '../../components/ui';

interface TestimonialForm {
  name: string;
  role: string;
  content: string;
  rating: number;
  is_published: boolean;
  avatar: File | null;
}

const emptyForm = (): TestimonialForm => ({
  name: '',
  role: '',
  content: '',
  rating: 5,
  is_published: true,
  avatar: null,
});

export default function Testimonials() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modal, setModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TestimonialForm>(emptyForm());
  const upload = useUpload();
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api.get<Testimonial[]>('/testimonials/admin/all').then(setItems).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openAdd = () => {
    setError('');
    setEditingId(null);
    setForm(emptyForm());
    setModal(true);
  };

  const openEdit = (t: Testimonial) => {
    setError('');
    setEditingId(t.id);
    setForm({
      name: t.name,
      role: t.role || '',
      content: t.content || '',
      rating: t.rating,
      is_published: t.is_published,
      avatar: null,
    });
    setModal(true);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('role', form.role);
      fd.append('content', form.content);
      fd.append('rating', String(form.rating));
      fd.append('is_published', form.is_published ? '1' : '0');
      if (form.avatar) fd.append('avatar', form.avatar);
      if (editingId) {
        await upload.run(`/testimonials/${editingId}`, fd, 'PUT');
      } else {
        await upload.run('/testimonials', fd);
      }
      setModal(false);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const remove = async (id: number) => {
    try {
      await api.delete(`/testimonials/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  if (loading) return <Spinner label="Loading testimonials..." />;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Testimonials</h1>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h2 className="text-lg font-semibold">Client Words</h2>
        <button onClick={openAdd} className={primaryBtn}>
          <Plus className="h-4 w-4" /> New Testimonial
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Published testimonials appear in the "Client Words" section on the homepage. Drafts are hidden from visitors.
      </p>

      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      {items.length === 0 ? (
        <EmptyState message="No testimonials yet. Click 'New Testimonial' to add the first one." />
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Testimonial</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        {t.avatar ? (
                          <img src={t.avatar} alt="" className="h-10 w-10 rounded-full object-cover mr-3 shrink-0" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-100 mr-3 shrink-0 flex items-center justify-center">
                            <MessageSquareQuote className="h-5 w-5 text-gray-300" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-800 capitalize">{t.name}</p>
                          {t.role && <p className="text-xs text-gray-500 capitalize">{t.role}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                      <p className="line-clamp-2">{t.content}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`h-4 w-4 ${n <= t.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {t.is_published ? (
                        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800">
                          <Eye className="h-3.5 w-3.5 mr-1" /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                          <EyeOff className="h-3.5 w-3.5 mr-1" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        <button onClick={() => openEdit(t)} className="p-2 rounded-lg hover:bg-primary/10 text-primary" title="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={() => setDeleteId(t.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={modal} onClose={() => setModal(false)} title={editingId ? 'Edit Testimonial' : 'New Testimonial'}>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className={labelClass}>Client Name *</label>
            <input
              className={inputClass}
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Adebayo Johnson"
            />
          </div>
          <div>
            <label className={labelClass}>Role / Title</label>
            <input
              className={inputClass}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="e.g. Property Investor, Lagos"
            />
          </div>
          <div>
            <label className={labelClass}>Testimonial *</label>
            <textarea
              className={inputClass}
              rows={5}
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="What did this client say about working with us?"
            ></textarea>
          </div>
          <div>
            <label className={labelClass}>Rating</label>
            <div className="flex items-center space-x-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  aria-label={`${n} star${n === 1 ? '' : 's'}`}
                >
                  <Star className={`h-7 w-7 ${n <= form.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Avatar / Photo</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className={inputClass}
              onChange={(e) => setForm({ ...form, avatar: e.target.files?.[0] || null })}
            />
          </div>
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span>Publish (visible on the homepage "Client Words" section)</span>
          </label>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModal(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Saving...' : editingId ? 'Save Changes' : 'Create Testimonial'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && remove(deleteId)}
        title="Delete Testimonial"
        message="Are you sure you want to delete this testimonial? This cannot be undone."
      />
    </div>
  );
}
